import { Button, classes, useDevicePlatform } from '@portal/components'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { RefreshCw } from 'react-feather'
import { useHotkeys } from 'react-hotkeys-hook'

import { shouldWrapInTicks } from '../../utils'
import { useDQLEditorContext } from '../dqlEditor/context'
import { SideBarOpener } from '../Sidebar'

const getPlaceholder = (collections?: string[]) => {
  if (!collections?.length) {
    return 'SELECT * FROM <collection> LIMIT 100 OFFSET 0'
  }

  let randomCollection =
    collections[Math.floor(Math.random() * collections.length)]
  if (shouldWrapInTicks(randomCollection)) {
    // A hyphen in the collection name without wrapping quotes seems to error in the DQL parser
    randomCollection = `\`${randomCollection}\``
  }

  return `SELECT * FROM ${randomCollection} LIMIT 100 OFFSET 0`
}

/**
 * The query editor for the DQLEditor. This component renders a textarea for the user to write
 * their DQL query, and updates the context with the query when the user clicks the "Run" button.
 */
export default function QueryEditor() {
  const { isMacOS } = useDevicePlatform()
  const {
    sideBarOpen,
    setSideBarOpen,
    historySideBarOpen,
    setHistorySideBarOpen,
    appCollections,
    query: activeQuery,
    setQuery: setActiveQuery,
    refetch,
    isLoading,
  } = useDQLEditorContext()
  const [query, setQuery] = useState(activeQuery || '')

  const lineNumbersRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setQuery(activeQuery || '')
  }, [activeQuery])

  const canRunQuery = query && !isLoading
  const handleSetQuery = useCallback(() => {
    if (!canRunQuery) {
      return
    } else if (query === activeQuery) {
      refetch()
    } else {
      setActiveQuery(query)
    }
  }, [canRunQuery, query, activeQuery, refetch, setActiveQuery])

  // `mod` should work for our needs without manual conditional logic: "listens for ctrl on Windows/Linux and cmd on macOS"
  // Refer to https://react-hotkeys-hook.vercel.app/docs/documentation/useHotkeys/basic-usage#modifiers--special-keys
  const textAreaRef = useHotkeys<HTMLTextAreaElement>(
    'mod+enter',
    handleSetQuery,
    {
      enableOnFormTags: true,
    },
    [handleSetQuery],
  )

  const handleTextareaScroll = () => {
    if (lineNumbersRef.current && textAreaRef.current) {
      lineNumbersRef.current.scrollTop = textAreaRef.current.scrollTop + 20
    }
  }

  const numRows = Math.max(query.split('\n').length, 9)
  const shortCut = isMacOS ? '⌘ + Return' : 'Ctrl + Enter'

  const fieldSizing = {
    'field-sizing': 'content',
  } as React.CSSProperties

  // TODO: The line number gutter is really scuffed, and it definitely needs to be replaced with something better.
  // I don't think it makes much sense to build and maintain our own SQL-supported editor, and I don't necessarily
  // feel Monaco is the right choice either. Since this is an MVP, I'm going to leave it as-is for now. However,
  // definitely something to revisit in the future.
  return (
    <>
      <header
        className={classes(
          'flex h-12 shrink-0 items-center gap-x-4 border-b border-solid border-border-normal px-4',
        )}
        data-testid="queryEditorHeader"
      >
        <SideBarOpener
          side="left"
          open={sideBarOpen}
          openHandler={() => setSideBarOpen(true)}
        >
          <h3 className="text-base font-medium text-foreground-normal">
            Query
          </h3>
        </SideBarOpener>

        <div className="flex-1" />

        <SideBarOpener
          side="right"
          open={historySideBarOpen}
          openHandler={() => setHistorySideBarOpen(true)}
        >
          <Button
            variant="primary"
            disabled={!query || isLoading}
            onClick={() => handleSetQuery()}
            data-testid="runQueryButton"
          >
            Run query <RefreshCw className="ml-3 h-4 w-4" />
          </Button>
        </SideBarOpener>
      </header>

      <div className="relative flex w-full border-b border-solid border-border-normal bg-background font-plex-mono">
        <div
          className="absolute left-0 z-30 flex max-h-full flex-col overflow-y-scroll px-3 pt-2 scrollbar-hide"
          ref={lineNumbersRef}
        >
          {Array.from({ length: numRows }).map((_, i) => (
            <span
              className="h-5 text-base text-foreground-subtle"
              key={`line-number-${i}`}
            >
              {i + 1}
            </span>
          ))}
        </div>

        <textarea
          ref={textAreaRef}
          className="field-sizing-content max-h-[50vh] min-h-[196px] w-full resize-none border-none bg-transparent pl-10 text-base leading-5 text-foreground-normal placeholder-foreground-subtle"
          style={fieldSizing}
          placeholder={getPlaceholder(appCollections)}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          data-testid="queryEditor"
          rows={9}
          onScroll={handleTextareaScroll}
          spellCheck={false}
        />

        <div className="absolute bottom-3 right-4" data-testid="shortCutHelp">
          <p className="text-base text-foreground-subtle">
            <span className="font-medium text-foreground-normal">
              {shortCut}
            </span>{' '}
            to run query
          </p>
        </div>
      </div>
    </>
  )
}
